// Google Calendar API configuration
const GOOGLE_API_KEY = "AIzaSyBFfe32kMVYB6-OQ1OTGOLU8fGnoli7gBY";
const GOOGLE_CLIENT_ID = '2342430703-5ub2n448mk49pj76b5r4q0sse409ld2c.apps.googleusercontent.com';
const DISCOVERY_DOCS = ['https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest'];
const SCOPES = 'https://www.googleapis.com/auth/calendar';

// Modern Google Identity Services approach
declare global {
  interface Window {
    google: any;
    gapi: any;
  }
}

interface CalendarEvent {
  summary: string;
  description?: string;
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  attendees?: Array<{ email: string }>;
  reminders?: {
    useDefault: boolean;
    overrides?: Array<{
      method: string;
      minutes: number;
    }>;
  };
}

interface UserInfo {
  email: string;
  name?: string;
  picture?: string;
}

class GoogleCalendarService {
  private gapi: any = null;
  private isInitialized = false;
  private tokenClient: any = null;

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Load Google Identity Services
      await this.loadGoogleIdentityServices();
      
      // Initialize the token client
      this.tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: SCOPES,
        callback: (tokenResponse: any) => {
          console.log('Token response received:', tokenResponse);
        },
      });

      // Load Google API client
      await this.loadGoogleApiClient();
      
      this.isInitialized = true;
      console.log('Google Calendar API initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Google Calendar API:', error);
      throw error;
    }
  }

  private async loadGoogleIdentityServices(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.google) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
      document.head.appendChild(script);
    });
  }

  private async loadGoogleApiClient(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.gapi) {
        this.gapi = window.gapi;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.gapi.load('client', async () => {
          try {
            await window.gapi.client.init({
              apiKey: GOOGLE_API_KEY,
              discoveryDocs: DISCOVERY_DOCS,
            });
            this.gapi = window.gapi;
            resolve();
          } catch (error) {
            reject(error);
          }
        });
      };
      script.onerror = () => reject(new Error('Failed to load Google API client'));
      document.head.appendChild(script);
    });
  }

  async authenticate(): Promise<boolean> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      console.log('Starting Google Calendar authentication...');
      console.log('Token client status:', !!this.tokenClient);
      console.log('GAPI status:', !!this.gapi);
      
      return new Promise((resolve, reject) => {
        // Set a timeout to prevent hanging
        const timeout = setTimeout(() => {
          reject(new Error('Authentication timeout - no response received'));
        }, 30000); // 30 seconds timeout

        this.tokenClient.callback = async (resp: any) => {
          clearTimeout(timeout);
          console.log('Token callback received:', resp);
          
          if (resp.error) {
            console.error('Authentication error:', resp.error);
            reject(new Error(`Authentication failed: ${resp.error}`));
            return;
          }

          if (resp.access_token) {
            console.log('Access token received successfully');
            // Set the token in gapi
            this.gapi.client.setToken(resp);
            resolve(true);
          } else {
            console.error('No access token in response:', resp);
            reject(new Error('No access token received'));
          }
        };

        try {
          if (this.gapi.client.getToken() === null) {
            console.log('No existing token, requesting new access token...');
            this.tokenClient.requestAccessToken({ prompt: 'consent' });
          } else {
            console.log('Existing token found, refreshing...');
            this.tokenClient.requestAccessToken({ prompt: '' });
          }
        } catch (tokenError) {
          clearTimeout(timeout);
          console.error('Error requesting access token:', tokenError);
          reject(new Error(`Token request failed: ${tokenError}`));
        }
      });
    } catch (error) {
      console.error('Authentication failed:', error);
      throw error;
    }
  }

  async getUserInfo(): Promise<UserInfo> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      // Try to get user info from the OAuth2 API
      const response = await this.gapi.client.oauth2.userinfo.get();
      return {
        email: response.result.email,
        name: response.result.name,
        picture: response.result.picture
      };
    } catch (error) {
      console.error('Failed to get user info from gapi:', error);
      
      // Fallback: try to get user info from the REST API
      const token = this.gapi.client.getToken();
      if (token && token.access_token) {
        try {
          const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
            headers: {
              'Authorization': `Bearer ${token.access_token}`
            }
          });
          const userInfo = await response.json();
          return {
            email: userInfo.email,
            name: userInfo.name,
            picture: userInfo.picture
          };
        } catch (fallbackError) {
          console.error('Failed to get user info from fallback:', fallbackError);
          throw new Error('Unable to retrieve user information');
        }
      } else {
        throw new Error('No access token available');
      }
    }
  }

  async createEvent(event: CalendarEvent): Promise<any> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      // Check if we have a valid token
      const token = this.gapi.client.getToken();
      if (!token || !token.access_token) {
        throw new Error('No access token available. Please authenticate first.');
      }

      console.log('Creating calendar event with data:', event);
      console.log('Using token:', token.access_token ? 'Valid token present' : 'No token');
      
      const response = await this.gapi.client.calendar.events.insert({
        calendarId: 'primary',
        resource: event,
      });
      
      console.log('Calendar event created successfully:', response.result);
      return response.result;
    } catch (error) {
      console.error('Failed to create calendar event:', error);
      
      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.message.includes('access_denied')) {
          throw new Error('Access denied. Please check your Google Calendar permissions.');
        } else if (error.message.includes('invalid_grant')) {
          throw new Error('Authentication expired. Please re-authenticate.');
        } else if (error.message.includes('quota_exceeded')) {
          throw new Error('Google Calendar quota exceeded. Please try again later.');
        } else if (error.message.includes('No access token')) {
          throw new Error('No access token available. Please authenticate first.');
        }
      }
      
      throw new Error(`Failed to create calendar event: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async listCalendars(): Promise<any[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const response = await this.gapi.client.calendar.calendarList.list();
      return response.result.items || [];
    } catch (error) {
      console.error('Failed to list calendars:', error);
      throw error;
    }
  }

  async getCalendar(calendarId: string): Promise<any> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const response = await this.gapi.client.calendar.calendarList.get({
        calendarId: calendarId,
      });
      return response.result;
    } catch (error) {
      console.error('Failed to get calendar:', error);
      throw error;
    }
  }

  async updateCalendar(calendarId: string, updates: any): Promise<any> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const response = await this.gapi.client.calendar.calendarList.update({
        calendarId: calendarId,
        resource: updates,
      });
      return response.result;
    } catch (error) {
      console.error('Failed to update calendar:', error);
      throw error;
    }
  }

  async patchCalendar(calendarId: string, updates: any): Promise<any> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const response = await this.gapi.client.calendar.calendarList.patch({
        calendarId: calendarId,
        resource: updates,
      });
      return response.result;
    } catch (error) {
      console.error('Failed to patch calendar:', error);
      throw error;
    }
  }

  async watchCalendar(calendarId: string, webhookUrl: string): Promise<any> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const response = await this.gapi.client.calendar.calendarList.watch({
        id: calendarId,
        resource: {
          id: `webhook-${Date.now()}`,
          type: 'web_hook',
          address: webhookUrl,
        },
      });
      return response.result;
    } catch (error) {
      console.error('Failed to watch calendar:', error);
      throw error;
    }
  }

  async insertCalendar(calendarData: any): Promise<any> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const response = await this.gapi.client.calendar.calendarList.insert({
        resource: calendarData,
      });
      return response.result;
    } catch (error) {
      console.error('Failed to insert calendar:', error);
      throw error;
    }
  }

  // Helper method to create a follow-up event
  async createFollowUpEvent(contactData: any, followUpDate: Date, notes?: string): Promise<any> {
    console.log('Creating follow-up event for contact:', contactData);
    console.log('Follow-up date:', followUpDate);
    console.log('Notes:', notes);
    
    // Check authentication status before proceeding
    const authStatus = await this.checkAuthenticationStatus();
    console.log('Authentication status:', authStatus);
    
    if (!authStatus.isAuthenticated) {
      throw new Error(`Authentication required: ${authStatus.message}`);
    }
    
    // Build description with contact details and notes
    let description = `Follow-up call/meeting with ${contactData.name} from ${contactData.companyName || 'Unknown Company'}. 
      
Contact Details:
- Email: ${contactData.email || 'N/A'}
- Phone: ${contactData.phone || 'N/A'}
- LinkedIn: ${contactData.linkedin || 'N/A'}
- Description: ${contactData.description || 'N/A'}`;

    // Add notes if provided
    if (notes && notes.trim()) {
      description += `\n\nFollow-up Notes:\n${notes}`;
    }
    
    const event: CalendarEvent = {
      summary: `Follow-up: ${contactData.name}${notes ? ` - ${notes}` : ''}`,
      description: description,
      start: {
        dateTime: followUpDate.toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      end: {
        dateTime: new Date(followUpDate.getTime() + 30 * 60000).toISOString(), // 30 minutes duration
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      reminders: {
        useDefault: false,
        overrides: [
          { method: 'email', minutes: 24 * 60 }, // 24 hours before
          { method: 'popup', minutes: 15 }, // 15 minutes before
        ],
      },
    };

    if (contactData.email) {
      event.attendees = [{ email: contactData.email }];
    }

    console.log('Created event object:', event);
    return await this.createEvent(event);
  }

  // Method to check authentication status
  async checkAuthenticationStatus(): Promise<{
    isAuthenticated: boolean;
    message: string;
    hasToken: boolean;
    tokenValid: boolean;
    gapiReady: boolean;
  }> {
    try {
      if (!this.isInitialized) {
        return {
          isAuthenticated: false,
          message: 'Service not initialized',
          hasToken: false,
          tokenValid: false,
          gapiReady: false
        };
      }

      if (!this.gapi || !this.gapi.client) {
        return {
          isAuthenticated: false,
          message: 'GAPI client not available',
          hasToken: false,
          tokenValid: false,
          gapiReady: false
        };
      }

      const token = this.gapi.client.getToken();
      const hasToken = !!(token && token.access_token);
      
      // Test if the token is valid by making a simple API call
      let tokenValid = false;
      if (hasToken) {
        try {
          await this.gapi.client.calendar.calendarList.list({ maxResults: 1 });
          tokenValid = true;
        } catch (error) {
          console.log('Token validation failed:', error);
          tokenValid = false;
        }
      }

      return {
        isAuthenticated: hasToken && tokenValid,
        message: hasToken && tokenValid ? 'Authenticated and ready' : 
                hasToken ? 'Has token but validation failed' : 'No token available',
        hasToken,
        tokenValid,
        gapiReady: true
      };
    } catch (error) {
      return {
        isAuthenticated: false,
        message: `Error checking status: ${error instanceof Error ? error.message : 'Unknown error'}`,
        hasToken: false,
        tokenValid: false,
        gapiReady: false
      };
    }
  }

  // Method to force re-authentication
  async reAuthenticate(): Promise<boolean> {
    try {
      console.log('Forcing re-authentication...');
      
      // Clear existing token
      if (this.gapi && this.gapi.client) {
        this.gapi.client.setToken(null);
      }
      
      // Reset initialization
      this.isInitialized = false;
      
      // Re-initialize and authenticate
      await this.initialize();
      return await this.authenticate();
    } catch (error) {
      console.error('Re-authentication failed:', error);
      throw error;
    }
  }
}

export default new GoogleCalendarService(); 